import { Vector3 } from 'three'
import type { BrickType, BrickDimensions } from '@models/brick'

export const BRICK_CONSTANTS = {
  STUD_DIAMETER: 0.4,    // Diameter of brick stud
  STUD_HEIGHT: 0.2,      // Height of stud
  BRICK_HEIGHT: 0.96,    // Standard brick height (3 plates)
  PLATE_HEIGHT: 0.32,    // Height of a plate
  UNIT_SIZE: 0.8,        // Size of one stud spacing
  STUD_SNAP_THRESHOLD: 0.1, // Distance threshold for stud snapping
} as const

/**
 * Get the dimensions for a given brick type
 *
 * @param type - The brick type (1x1, 1x2, 2x2, 2x4, 1x4)
 * @returns Dimensions in world units (width, length, height)
 */
export function getBrickDimensions(type: BrickType): BrickDimensions {
  const { UNIT_SIZE, BRICK_HEIGHT } = BRICK_CONSTANTS

  switch (type) {
    case '1x1':
      return { width: UNIT_SIZE, length: UNIT_SIZE, height: BRICK_HEIGHT }
    case '1x2':
      return { width: UNIT_SIZE, length: UNIT_SIZE * 2, height: BRICK_HEIGHT }
    case '2x2':
      return { width: UNIT_SIZE * 2, length: UNIT_SIZE * 2, height: BRICK_HEIGHT }
    case '2x4':
      return { width: UNIT_SIZE * 2, length: UNIT_SIZE * 4, height: BRICK_HEIGHT }
    case '1x4':
      return { width: UNIT_SIZE, length: UNIT_SIZE * 4, height: BRICK_HEIGHT }
    default:
      return { width: UNIT_SIZE, length: UNIT_SIZE, height: BRICK_HEIGHT }
  }
}

/**
 * Get the number of studs for a brick type
 *
 * @param type - The brick type
 * @returns Object with studsX (width) and studsZ (length) counts
 */
export function getStudCounts(type: BrickType): { studsX: number; studsZ: number } {
  switch (type) {
    case '1x1':
      return { studsX: 1, studsZ: 1 }
    case '1x2':
      return { studsX: 1, studsZ: 2 }
    case '2x2':
      return { studsX: 2, studsZ: 2 }
    case '2x4':
      return { studsX: 2, studsZ: 4 }
    case '1x4':
      return { studsX: 1, studsZ: 4 }
    default:
      return { studsX: 1, studsZ: 1 }
  }
}

/**
 * Generate stud positions relative to brick center
 *
 * Studs are positioned on top of the brick surface, centered on each unit cell.
 * For a 1x1 brick: 1 stud at (0, height/2 + stud_height/2, 0)
 * For a 2x2 brick: 4 studs in a grid pattern
 *
 * @param type - The brick type
 * @param rotation - Rotation in degrees (0, 90, 180, 270) - affects stud pattern for non-square bricks
 * @returns Array of Vector3 positions relative to brick center
 */
export function generateStudPositions(type: BrickType, rotation: number = 0): Vector3[] {
  const { studsX, studsZ } = getStudCounts(type)
  const { UNIT_SIZE, BRICK_HEIGHT, STUD_HEIGHT } = BRICK_CONSTANTS
  const studY = BRICK_HEIGHT / 2 + STUD_HEIGHT / 2

  const positions: Vector3[] = []

  // Calculate stud grid offset (center the studs on the brick)
  const offsetX = -(studsX - 1) * UNIT_SIZE / 2
  const offsetZ = -(studsZ - 1) * UNIT_SIZE / 2

  // Generate stud positions in a grid
  for (let x = 0; x < studsX; x++) {
    for (let z = 0; z < studsZ; z++) {
      const localX = offsetX + x * UNIT_SIZE
      const localZ = offsetZ + z * UNIT_SIZE

      // Apply rotation if needed (for non-square bricks)
      const radians = (rotation * Math.PI) / 180
      const rotatedX = localX * Math.cos(radians) - localZ * Math.sin(radians)
      const rotatedZ = localX * Math.sin(radians) + localZ * Math.cos(radians)

      positions.push(new Vector3(rotatedX, studY, rotatedZ))
    }
  }

  return positions
}

/**
 * Generate socket positions on the bottom of a brick
 *
 * Sockets mirror the stud positions but are on the bottom surface.
 * Used for detecting valid connections when stacking.
 *
 * @param type - The brick type
 * @param rotation - Rotation in degrees
 * @returns Array of Vector3 positions relative to brick center
 */
export function generateSocketPositions(type: BrickType, rotation: number = 0): Vector3[] {
  const { studsX, studsZ } = getStudCounts(type)
  const { UNIT_SIZE, BRICK_HEIGHT } = BRICK_CONSTANTS
  const socketY = -BRICK_HEIGHT / 2

  const positions: Vector3[] = []

  // Calculate socket grid offset
  const offsetX = -(studsX - 1) * UNIT_SIZE / 2
  const offsetZ = -(studsZ - 1) * UNIT_SIZE / 2

  // Generate socket positions matching stud pattern
  for (let x = 0; x < studsX; x++) {
    for (let z = 0; z < studsZ; z++) {
      const localX = offsetX + x * UNIT_SIZE
      const localZ = offsetZ + z * UNIT_SIZE

      // Apply rotation
      const radians = (rotation * Math.PI) / 180
      const rotatedX = localX * Math.cos(radians) - localZ * Math.sin(radians)
      const rotatedZ = localX * Math.sin(radians) + localZ * Math.cos(radians)

      positions.push(new Vector3(rotatedX, socketY, rotatedZ))
    }
  }

  return positions
}

/**
 * Get world-space stud positions for a placed brick
 *
 * @param type - The brick type
 * @param position - World position of brick center
 * @param rotation - Rotation in degrees
 * @returns Array of Vector3 positions in world space
 */
export function getWorldStudPositions(
  type: BrickType,
  position: Vector3,
  rotation: number
): Vector3[] {
  const localPositions = generateStudPositions(type, rotation)
  return localPositions.map(local =>
    new Vector3(
      position.x + local.x,
      position.y + local.y,
      position.z + local.z
    )
  )
}
